import { jsPDF } from 'jspdf';

/**
 * Font names used in jsPDF
 * These names are registered via addFont() and can be used with setFont()
 */
export const FONT_NAMES = {
    LATO: 'Lato',
    DEJAVU_SANS: 'DejaVuSans'
} as const;

// Import base64 font files
// Lato fonts
import { LATO_REGULAR_BASE64 } from './fonts/Lato-Regular';
import { LATO_BOLD_BASE64 } from './fonts/Lato-Bold';
import { LATO_ITALIC_BASE64 } from './fonts/Lato-Italic';
import { LATO_BOLDITALIC_BASE64 } from './fonts/Lato-BoldItalic';

// DejaVu Sans fonts (for Cyrillic support)
// import { DEJAVU_SANS_BASE64 } from './fonts/DejaVuSans';
// import { DEJAVU_SANS_BOLD_BASE64 } from './fonts/DejaVuSans-Bold';
// import { DEJAVU_SANS_ITALIC_BASE64 } from './fonts/DejaVuSans-Italic';
// import { DEJAVU_SANS_BOLDITALIC_BASE64 } from './fonts/DejaVuSans-BoldItalic';

/**
 * jsPDF decodes VFS entries with atob(). Bundled font strings must be strict
 * base64: no whitespace/newlines, optional data-URL prefix stripped, padding fixed.
 */
function prepareBase64ForJsPdf(raw: string | undefined): string {
    if (raw == null || typeof raw !== 'string') {
        return '';
    }
    let s = raw.trim();
    const dataMatch = /^data:[^;]+;base64,([\s\S]*)$/i.exec(s);
    if (dataMatch && dataMatch[1] != null) {
        s = dataMatch[1].trim();
    }
    s = s.replace(/\s/g, '');
    // URL-safe base64 → standard (atob is picky in browsers)
    s = s.replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4 !== 0) {
        s += '=';
    }
    return s;
}

/**
 * Track initialization per jsPDF document instance.
 * Fonts are registered on a jsPDF instance, so using a single global flag breaks
 * subsequent documents (autoTable will warn that the font label cannot be found).
 */
const initializedDocs = new WeakSet<object>();

/**
 * Initialize custom fonts in jsPDF document
 * This function must be called once before using custom fonts
 * 
 * @param doc - jsPDF document instance
 * @throws Error if font initialization fails
 */
export function initializeFonts(doc: jsPDF): void {
    const docRef = doc as unknown as object;
    if (initializedDocs.has(docRef)) {
        // Fonts already initialized for this document, skip
        return;
    }

    try {
        const reg = prepareBase64ForJsPdf(LATO_REGULAR_BASE64);
        const bold = prepareBase64ForJsPdf(LATO_BOLD_BASE64);
        const italic = prepareBase64ForJsPdf(LATO_ITALIC_BASE64);
        const boldItalic = prepareBase64ForJsPdf(LATO_BOLDITALIC_BASE64);

        const fonts: Array<[
            string,
            string,
            typeof FONT_NAMES.LATO,
            'normal' | 'bold' | 'italic' | 'bolditalic'
        ]> = [
            ['Lato-Regular.ttf', reg, FONT_NAMES.LATO, 'normal'],
            ['Lato-Bold.ttf', bold, FONT_NAMES.LATO, 'bold'],
            ['Lato-Italic.ttf', italic, FONT_NAMES.LATO, 'italic'],
            ['Lato-BoldItalic.ttf', boldItalic, FONT_NAMES.LATO, 'bolditalic'],
        ];

        for (const [vfsName, b64, fontName, style] of fonts) {
            if (!b64 || b64.length < 64) {
                throw new Error(`Font payload empty or too short: ${vfsName}`);
            }
            doc.addFileToVFS(vfsName, b64);
            doc.addFont(vfsName, fontName, style);
        }

        initializedDocs.add(docRef);
        console.log('✓ Custom fonts initialized successfully');
    } catch (error) {
        console.error('✗ Error initializing fonts:', error);
        throw new Error(`Failed to initialize fonts: ${error instanceof Error ? error.message : String(error)}`);
    }
}

/**
 * Set font for document based on language
 * Currently using Lato for all languages (Lato supports Cyrillic)
 * If Cyrillic doesn't render correctly, add DejaVu Sans and use it for Russian
 * 
 * @param doc - jsPDF document instance
 * @param language - Language code ('et', 'en', or 'ru')
 */
export function setFontForLanguage(doc: jsPDF, language: 'et' | 'en' | 'ru'): void {
    // Use Lato for all languages (Lato supports Cyrillic)
    // If Cyrillic doesn't render correctly, uncomment DejaVu Sans code below
    doc.setFont(FONT_NAMES.LATO, 'normal');
    
    // TODO: If Cyrillic doesn't render correctly, uncomment this:
    // if (language === 'ru') {
    //     // Use DejaVu Sans for Cyrillic support
    //     doc.setFont(FONT_NAMES.DEJAVU_SANS, 'normal');
    // } else {
    //     // Use Lato for other languages (Estonian, English)
    //     doc.setFont(FONT_NAMES.LATO, 'normal');
    // }
}

/**
 * Set font style for the document
 * 
 * @param doc - jsPDF document instance
 * @param fontName - Font name (from FONT_NAMES)
 * @param style - Font style ('normal', 'bold', 'italic', 'bolditalic')
 */
export function setFontStyle(
    doc: jsPDF,
    fontName: typeof FONT_NAMES[keyof typeof FONT_NAMES],
    style: 'normal' | 'bold' | 'italic' | 'bolditalic'
): void {
    doc.setFont(fontName, style);
}

/**
 * Reset fonts initialization flag (useful for testing)
 */
export function resetFontInitialization(): void {
    // No-op: initialization is tracked per jsPDF instance via WeakSet
}

